import { useState, useCallback, useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Routes from '@/router/Routes';
import BackButton from '@/components/ui/BackButton/BackButton';
import Stepper from '@/components/shared/Stepper/Stepper';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext';
import TextArea from '@/components/functional/TextArea/TextArea';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import { useCreateLesson, useLesson, useUpdateLesson } from '@/api/useLessons';
import { useRegions } from '@/api/useRegions';
import { useSchools } from '@/api/useSchools';
import { useClasses } from '@/api/useClasses';
import { useSubjects } from '@/api/useSubjects';
// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs';
// @ts-ignore
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface LessonFormProps {
  editId?: string;
}

const STEPS = ['destination', 'language', 'contentChoice', 'contentEdit', 'video', 'preview'] as const;

const STEP_LABELS: Record<string, string> = {
  destination: 'adminUploadLesson.stepDestination',
  language: 'adminUploadLesson.stepLanguage',
  contentChoice: 'adminUploadLesson.stepContentChoice',
  contentEdit: 'adminUploadLesson.stepContentEdit',
  video: 'adminUploadLesson.stepVideo',
  preview: 'adminUploadLesson.stepPreview',
};

function extractYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  return null;
}

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor"/>
  </svg>
);

const StepperContent = ({ editId }: LessonFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nextStep, currentStepIndex, step } = useStepperContext();
  const createLesson = useCreateLesson();
  const updateLesson = useUpdateLesson();
  const { data: existingLesson } = useLesson(editId);

  const isEditMode = !!editId;

  const [content, setContent] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedLesson, setSelectedLesson] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (existingLesson && !initialized) {
      setSelectedLesson(existingLesson.title ?? '');
      setSelectedLanguage(existingLesson.language ?? '');
      setContent(existingLesson.content ?? '');
      setVideoUrl(existingLesson.video_url ?? '');
      if (existingLesson.region_id) setSelectedRegion(existingLesson.region_id);
      if (existingLesson.school_id) setSelectedSchool(existingLesson.school_id);
      if (existingLesson.class_id) setSelectedClass(existingLesson.class_id);
      if (existingLesson.subject_id) setSelectedSubject(existingLesson.subject_id);
      setInitialized(true);
    }
  }, [existingLesson, initialized]);

  const { data: regions = [] } = useRegions();
  const { data: schools = [] } = useSchools(selectedRegion || undefined);
  const { data: classes = [] } = useClasses(selectedSchool || undefined);
  const { data: subjects = [] } = useSubjects(selectedClass || undefined);

  const regionOptions = regions.map((r) => ({ value: r.id, label: r.name }));
  const schoolOptions = schools.map((s) => ({ value: s.id, label: s.name }));
  const classOptions = classes.map((c) => ({ value: c.id, label: c.name }));
  const subjectOptions = subjects.map((s) => ({ value: s.id, label: s.name }));

  const totalSteps = STEPS.length;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;
  const stepLabel = t(STEP_LABELS[step] ?? '');

  const embedUrl = extractYouTubeEmbedUrl(videoUrl);

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md,.doc,.docx,.pdf';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (file.type === 'application/pdf') {
        const arrayBuffer = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pages: string[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const textContent = await page.getTextContent();
          pages.push(textContent.items.map((item: any) => ('str' in item ? item.str : '')).join(' '));
        }
        setContent(pages.join('\n\n'));
        nextStep();
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        setContent(ev.target?.result as string);
        nextStep();
      };
      reader.readAsText(file);
    };
    input.click();
  }, [nextStep]);

  const handleAddManually = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const handleApproveAndPublish = async () => {
    const title = selectedLesson || 'Untitled Lesson';
    const subjectName = subjects.find((s) => s.id === selectedSubject)?.name ?? selectedSubject;
    const className = classes.find((c) => c.id === selectedClass)?.name ?? selectedClass;
    const payload = {
      title,
      subject: subjectName || null,
      class: className || null,
      language: selectedLanguage || null,
      content: content || null,
      video_url: videoUrl || null,
      region_id: selectedRegion || null,
      school_id: selectedSchool || null,
      class_id: selectedClass || null,
      subject_id: selectedSubject || null,
    };
    if (isEditMode && editId) {
      await updateLesson.mutateAsync({ id: editId, ...payload });
    } else {
      await createLesson.mutateAsync(payload);
    }
    navigate(Routes.ADMIN_DASHBOARD);
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex gap-1 mb-1 text-sm">
          <span className="text-[#00000099] font-semibold">{stepLabel}</span>
          <span className="text-[#FF6B6B]">- {currentStepIndex + 1}/{totalSteps}</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 4,
            bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#FF6B6B',
              borderRadius: 4,
            },
          }}
        />
      </div>

      <Stepper.Step step="destination">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <SelectInput
            value={selectedRegion}
            onChange={(val) => { setSelectedRegion(val); setSelectedSchool(''); setSelectedClass(''); setSelectedSubject(''); }}
            options={regionOptions}
            placeholder={t('adminAddRegion.selectRegion') || 'Select Region'}
          />
          <SelectInput
            value={selectedSchool}
            onChange={(val) => { setSelectedSchool(val); setSelectedClass(''); setSelectedSubject(''); }}
            options={schoolOptions}
            placeholder={t('adminAddRegion.selectSchool')}
          />
          <SelectInput
            value={selectedClass}
            onChange={(val) => { setSelectedClass(val); setSelectedSubject(''); }}
            options={classOptions}
            placeholder={t('adminUploadContent.selectClass')}
          />
          <SelectInput
            value={selectedSubject}
            onChange={setSelectedSubject}
            options={subjectOptions}
            placeholder={t('adminUploadContent.selectSubject')}
          />
          <input
            type="text"
            value={selectedLesson}
            onChange={(e) => setSelectedLesson(e.target.value)}
            placeholder={t('adminUploadContent.selectLesson')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />
          <button
            onClick={nextStep}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      <Stepper.Step step="language">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <SelectInput
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            options={[
              { value: 'en', label: 'English' },
              { value: 'hi', label: 'Hindi' },
              { value: 'bn', label: 'Bengali' },
              { value: 'ta', label: 'Tamil' },
              { value: 'te', label: 'Telugu' },
            ]}
            placeholder={t('adminUploadContent.selectLanguage')}
          />
          <button
            onClick={nextStep}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      <Stepper.Step step="contentChoice">
        <div className="flex flex-col items-center justify-center gap-4 min-h-[400px]">
          <button
            onClick={handleFileUpload}
            className="w-full max-w-[280px] h-[44px] flex items-center justify-center gap-2 bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-[120px] text-sm font-bold transition-colors"
          >
            {t('adminUploadLesson.uploadContent')}
          </button>
          <button
            onClick={handleAddManually}
            className="w-full max-w-[280px] h-[44px] flex items-center justify-center gap-2 bg-white border border-[#FF6B6B] text-[#FF6B6B] rounded-[120px] text-sm font-bold transition-colors hover:bg-[#FFF5F5]"
          >
            {t('adminUploadLesson.addManually')}
          </button>
        </div>
      </Stepper.Step>

      <Stepper.Step step="contentEdit">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('adminUploadContent.textPlaceholder')}
            rows={10}
            className="w-full h-full rounded-[32px] py-[30px] px-[16px] text-[15px] font-[400] text-[#000000de] focus:outline-none focus:ring-0"
          />
          <button
            onClick={nextStep}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      <Stepper.Step step="video">
        <div className="flex flex-col items-center justify-center gap-4 min-h-[400px]">
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder={t('adminUploadLesson.videoUrlPlaceholder')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />
          {embedUrl && (
            <button
              onClick={nextStep}
              className="w-full max-w-[280px] h-[44px] flex items-center justify-center gap-2 bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-[120px] text-sm font-bold transition-colors"
            >
              <UploadIcon />
              {t('adminUploadLesson.uploadMedia')}
            </button>
          )}
          <button
            onClick={nextStep}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center"
          >
            {embedUrl ? t('common.next') : t('adminUploadLesson.skipVideo')}
          </button>
        </div>
      </Stepper.Step>

      <Stepper.Step step="preview">
        <div className="flex flex-col gap-4 min-h-[400px]">
          {embedUrl && (
            <div className="aspect-video w-full rounded-3xl overflow-hidden bg-black">
              <iframe
                width="100%"
                height="100%"
                src={embedUrl}
                title="Lesson Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          <button
            onClick={handleApproveAndPublish}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center"
          >
            {isEditMode ? t('button.save') : t('adminUploadContent.approveAndPublish')}
          </button>
        </div>
      </Stepper.Step>
    </>
  );
};

const AdminUploadLessonPage = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <div className="flex flex-col gap-6 relative">
      <div className="flex items-center gap-2 mb-4">
        <BackButton />
      </div>

      <Stepper.Wrapper steps={STEPS} initialStep="destination">
        <StepperContent editId={id} />
      </Stepper.Wrapper>
    </div>
  );
};

export default AdminUploadLessonPage;
